import api, { realTimeApiInstance } from '../api';
import dayjs from 'dayjs';
import { createWrapperPayload, wrapperConstants } from '../wrapper-api';
import { formatDate } from '../../utilities/dateHelpers';

export const fetchAssetTrendDataInitialState = async (
  assetId: string,
  startDate: Date,
  endDate: Date,
  aggregate: string,
  aggregateMethod: string,
  FetchMinMax: boolean,
  FetchStdDeviation: boolean,
  FetchCov: boolean,
  rodlift?: boolean,
  Addresses?: string,
) => {
  const processedResponse = {
    data: [],
    rawData: [],
  };
  try {
    const headers: Record<string, string> = {};
    if (rodlift) {
      headers['x-api-version'] = '2.0';
    }

    const response = await realTimeApiInstance.get('RealTimeData/GetAssetTrendsGraphData', {
      params: {
        assetId,
        startDate: dayjs(startDate).format('YYYY-MM-DD HH:mm:ss.SSS'),
        endDate: dayjs(endDate).format('YYYY-MM-DD HH:mm:ss.SSS'),
        aggregate,
        aggregateMethod,
        FetchMinMax,
        FetchStdDeviation,
        FetchCov,
        ...(Addresses && Addresses !== '' ? { Addresses: Addresses || '' } : {}),
      },
      headers,
    });

    if (response && response.data) {
      processedResponse.data = transformApiDataToChartData(response.data);
      processedResponse.rawData = response.data;
      return processedResponse;
    }
    return processedResponse;
  } catch (error) {
    console.error('Error fetching asset trend data:', error);
    return processedResponse;
  }
};

const transformApiDataToChartData = (apiData: any): any => {
  return apiData.map((item: any, index: any) => {
    // Transform data points
    const dataPoints = item.dataPoints
      ? item.dataPoints.map((point: any, pointIndex: any) => ({
          date: new Date(point.date).getTime(),
          value: parseFloat(point.value),
          index: pointIndex,
          cov: parseFloat(point.cov),
          cumulativeCOV: parseFloat(point.cumulativeCOV),
          stdDevValue: parseFloat(item.stdDevValue),
          stdDevNegValue: parseFloat(item.stdDevNegValue),
          stdDevPosValue: parseFloat(item.stdDevPosValue),
        }))
      : [];

    // Transform minThresholdValues
    const minThresholdValues = item.minThresholdValues
      ? item.minThresholdValues.map((value: any, pointIndex: any) => ({
          date: new Date(value.date).getTime(),
          value: parseFloat(value.value),
          index: pointIndex,
        }))
      : [];

    // Transform minThresholdValues
    const maxThresholdValues = item.maxThresholdValues
      ? item.maxThresholdValues.map((value: any, pointIndex: any) => ({
          date: new Date(value.date).getTime(),
          value: parseFloat(value.value),
          index: pointIndex,
        }))
      : [];

    // Sort data points by date
    dataPoints.sort((a: any, b: any) => a.date - b.date);

    // Assign index after sorting
    dataPoints.forEach((point: any, idx: any) => {
      point.index = idx;
    });

    // const currentTrendData = parameterData?.find((trend: any) => trend.name === item.trendName);
    return {
      id: `chart-${index}`,
      address: item.address,
      title: item.trendName,
      color: '#012F6D',
      unitOfMeasure: item.unitOfMeasure,
      shortUnitOfMeasure: item.short_UnitOfMeasure,
      value: item.value,
      maxValue: item.max,
      minValue: item.min,
      medianValue: item.median,
      date: formatDate(item.date, true),
      dataPoints: dataPoints,
      maxThresholdValues: maxThresholdValues,
      minThresholdValues: minThresholdValues,
      cov: item.cov,
      cumulativeCOV: item.cumulativeCOV,
      // lineColor: currentTrendData?.lineColor,
      // markerColor: currentTrendData?.markerColor,
      // markerType: currentTrendData?.markerType,
      // displayOrder: currentTrendData?.displayOrder,
      // selected: currentTrendData?.selected,
    };
  });
};

export const fetchWellTestDataForCharts = async (wellId: string, startDate: Date, endDate: Date) => {
  try {
    const requestPayload = createWrapperPayload('POST', '/pumpchecker/WellTest/GetWellTestDataForCharts', {
      wellId,
      startDate,
      endDate,
    });
    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      }
    });
  } catch (error) {
    console.error('Error fetching asset trend data:', error);
    return [];
  }
};

export const fetchAssetTrendDataByTags = async (
  assetId: string,
  startDate: Date,
  endDate: Date,
  aggregate: string,
  aggregateMethod: string,
  paramStdTypes: (string | number)[] = [],
  assetGUID?: string,
) => {
  const response = {
    data: [],
  };
  try {
    const headers: Record<string, string> = {};
    if (assetGUID) {
      headers['x-api-version'] = '2.0';
    }

    const response = await realTimeApiInstance.get('RealTimeData/GetAssetTrendsGraphDataByTags', {
      params: {
        assetId,
        startDate: dayjs(startDate).format('YYYY-MM-DD HH:mm:ss.SSS'),
        endDate: dayjs(endDate).format('YYYY-MM-DD HH:mm:ss.SSS'),
        aggregate,
        aggregateMethod,
        paramStdTypes: Array.isArray(paramStdTypes) ? paramStdTypes.join(',') : paramStdTypes,
      },
    });

    if (response && response.data) {
      return response;
    }
    return response;
  } catch (error) {
    console.error('Error fetching asset trend data by tags:', error);
    return response;
  }
};
